import React from 'react'
import { useTranslation } from 'react-i18next'
import network from '../../services/Network'
import cloudController from '../../services/cloudController'
import cloudSync from '../../services/CloudSync'
import { emit } from '../../services/Controller'
import { Dispatch, State } from '../../store'
import { VALID_JOB_ID_LENGTH, GUIDE_START_DATE } from '../../constants'
import { useParams, useRouteMatch } from 'react-router-dom'
import { selectDeviceModelAttributes, selectDevice } from '../../selectors/devices'
import { useDispatch, useSelector } from 'react-redux'
import { IconButton, ButtonProps } from '../IconButton'
import { GuideBubble } from '../../components/GuideBubble'
import { Typography } from '@mui/material'

export const RefreshButton: React.FC<ButtonProps> = props => {
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const { deviceID, fileID, jobID, userId, partnerId } = useParams<{
    deviceID?: string
    fileID?: string
    jobID?: string
    userId?: string
    partnerId?: string
  }>()
  const device = useSelector((state: State) => selectDevice(state, undefined, deviceID))
  const fetching = useSelector(
    (state: State) =>
      selectDeviceModelAttributes(state).fetching || (deviceID && state.logs.fetching) || state.ui.fetching
  )
  const connectionPage = useRouteMatch('/connections')
  const networkPage = useRouteMatch('/networks')
  const logsPage = useRouteMatch(['/logs', '/devices/:deviceID/logs'])
  const devicesPage = useRouteMatch('/devices')
  const addDevicePage = useRouteMatch(['/add', '/onboard'])
  const productsPage = useRouteMatch('/products')
  const accountPage = useRouteMatch('/account')
  const partnerStatsPage = useRouteMatch('/partner-stats')
  const adminUsersPage = useRouteMatch('/admin/users')
  const adminPartnersPage = useRouteMatch('/admin/partners')
  const adminEnterpriseLicensesPage = useRouteMatch('/admin/enterprise-licenses')
  const adminNoticesPage = useRouteMatch('/admin/notices')
  const scriptingPage = useRouteMatch(['/script', '/scripts', '/runs'])
  const runsPage = useRouteMatch<{ fileID?: string }>('/runs/:fileID?')
  const scriptPage = useRouteMatch('/script')

  // The /admin section is deliberately English-only, so its titles below stay
  // untranslated - see the admin pages, none of which use useTranslation.
  let title = t('refreshButton.application', 'Refresh application')
  let methods: Methods = []

  // connection pages
  if (connectionPage) {
    title = t('refreshButton.connections', 'Refresh connections')
    methods.push(dispatch.connections.fetch)

    // network pages
  } else if (networkPage) {
    title = t('refreshButton.networks', 'Refresh networks')
    methods.push(dispatch.networks.fetchAll)

    // scripting pages
  } else if (scriptingPage) {
    title = t('refreshButton.scripts', 'Refresh scripts')
    methods.push(dispatch.files.fetch)
    const runsFileID = runsPage?.params.fileID
    methods.push(async () => await dispatch.jobs.fetch({ fileID: runsFileID }))

    // script pages
  } else if (scriptPage) {
    title = t('refreshButton.script', 'Refresh script')
    if (fileID) methods.push(async () => await dispatch.files.fetchSingle({ fileId: fileID }))
    if (jobID && jobID.length >= VALID_JOB_ID_LENGTH)
      methods.push(async () => await dispatch.jobs.fetchSingle({ jobId: jobID }))
    else if (fileID) methods.push(async () => await dispatch.jobs.fetchByFileIds({ fileIds: [fileID] }))

    // log pages
  } else if (logsPage) {
    title = device
      ? t('refreshButton.deviceLogs', { name: device.name, defaultValue: 'Refresh {{name}} logs' })
      : t('refreshButton.logs', 'Refresh logs')
    methods.push(async () => {
      if (device) await dispatch.devices.fetchSingleFull({ id: device.id })
      await dispatch.logs.set({ after: undefined, maxDate: undefined })
      await dispatch.logs.fetch({ deviceId: device?.id })
    })

    // device pages, and the add device pages and sub pages - those carry no
    // deviceID, so they take the list branch and a newly registered device shows
    // up without leaving the add flow
  } else if (devicesPage || addDevicePage) {
    title = device
      ? t('refreshButton.device', { name: device.name, defaultValue: 'Refresh {{name}}' })
      : t('refreshButton.devices', 'Refresh devices')
    methods.push(async () => {
      if (device) {
        await dispatch.devices.fetchSingleFull({ id: device.id })
      } else {
        await dispatch.devices.set({ from: 0 })
        await dispatch.devices.fetchList()
      }
    })

    // products pages
  } else if (productsPage) {
    title = t('refreshButton.products', 'Refresh products')
    methods.push(dispatch.products.fetch)

    // account section (any /account/* page) — refresh the connected apps the section owns
  } else if (accountPage) {
    title = t('refreshButton.account', 'Refresh account')
    methods.push(dispatch.agents.fetch)

    // partner stats pages
  } else if (partnerStatsPage) {
    title = t('refreshButton.partnerStats', 'Refresh partner stats')
    methods.push(dispatch.partnerStats.fetch)

    // admin users pages
  } else if (adminUsersPage) {
    title = userId ? 'Refresh user' : 'Refresh users'
    methods.push(async () => await dispatch.adminUsers.fetch(undefined))
    if (userId)
      methods.push(async () => {
        await dispatch.adminUsers.fetchUserDetail({ userId, force: true })
      })

    // admin partners pages
  } else if (adminPartnersPage) {
    title = partnerId ? 'Refresh partner' : 'Refresh partners'
    methods.push(async () => await dispatch.adminPartners.fetch())
    if (partnerId)
      methods.push(async () => {
        await dispatch.adminPartners.invalidatePartnerDetail(partnerId)
        await dispatch.adminPartners.fetchPartnerDetail(partnerId)
      })

    // admin enterprise licenses pages
  } else if (adminEnterpriseLicensesPage) {
    title = 'Refresh enterprise customers'
    methods.push(async () => await dispatch.adminEnterpriseLicenses.fetch())

    // admin notices pages
  } else if (adminNoticesPage) {
    title = 'Refresh notices'
    methods.push(async () => await dispatch.adminNotices.fetch())
  }

  const refresh = async () => {
    if (fetching) {
      await cloudSync.cancel()
      await dispatch.devices.set({ fetching: false })
      return
    }
    await cloudSync.call(methods)
    await cloudSync.core(!methods.length)
    dispatch.devices.expire()
    cloudController.ping()
    network.connect()
    emit('refresh')
  }

  return (
    <GuideBubble
      highlight
      queueAfter="addDevice"
      guide="refresh"
      placement="bottom"
      startDate={GUIDE_START_DATE}
      added={GUIDE_START_DATE}
      instructions={
        <>
          <Typography variant="h3" gutterBottom>
            <b>{t('refreshButton.guideTitle', 'Refresh the application')}</b>
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t(
              'refreshButton.guideBody',
              'Device state will update automatically while your system is on and the app is running.'
            )}
          </Typography>
        </>
      }
    >
      <IconButton
        {...props}
        fixedWidth
        icon="sync"
        placement="bottom"
        color={fetching ? 'gray' : props.color}
        title={title}
        spin={fetching}
        onClick={refresh}
      />
    </GuideBubble>
  )
}
